/**
 * The product page's exact quote: its selection priced at the REAL line
 * quantities by `POST /api/storefront/<slug>/products/<id>/quote`.
 *
 * The page's own size prices are `price_for` at quantity 1. Store pricing
 * steps down with the line quantity, so a total built from them is only an
 * estimate (#3884 gate P1-1). The quote endpoint prices each line through the
 * same `price_for` the cart and checkout use, so the total it yields is the
 * one the cart shows for the same lines. The cart stays the price of record:
 * add-to-cart merges into an existing line, and a larger line can only price
 * the same or lower.
 */

// Wait this long after the last quantity / colour change before asking, so
// typing "12" sends one request, not two.
export const QUOTE_DEBOUNCE_MS = 300;

/** The quote URL for a store slug and product id. */
export function pdpQuoteUrl(slug, productId) {
  return `/api/storefront/${encodeURIComponent(slug)}/products/${encodeURIComponent(
    productId
  )}/quote`;
}

/**
 * Ask for a quote; resolve to `{ [size]: unitCents }`, or null when ANY
 * requested line is missing from the answer or has no price.
 *
 * Null means "no exact total": the caller keeps its labelled estimate. A
 * partial answer is never used, since a total mixing exact and estimated
 * lines would be shown as exact while being neither. Rejects on a transport
 * or HTTP error (the caller treats that as null too).
 *
 * `lines` is `[{ size, quantity }]`, at most one entry per size.
 */
export async function fetchPdpQuote(axios, slug, productId, color, lines) {
  const response = await axios.post(pdpQuoteUrl(slug, productId), {
    color,
    lines,
  });
  const answered = response && response.data && response.data.lines;
  if (!Array.isArray(answered)) return null;

  const unitBySize = {};
  for (const line of answered) {
    const cents = line ? line.unit_price_cents : null;
    if (Number.isInteger(cents) && cents >= 0) {
      unitBySize[line.size] = cents;
    }
  }
  for (const line of lines) {
    if (unitBySize[line.size] === undefined) return null;
  }
  return unitBySize;
}
